/**
 * The pieces behind one bill: every garment counted in the bill's period,
 * newest first (`billing.listPieces`).
 *
 * Filter, count and paging live in ONE layer, the server's RPC (CLAUDE.md
 * §10): the search and the client go into the query input, the server pages
 * by keyset and returns the FILTERED total, so "Showing n of N" always
 * describes the rows on screen. Nothing is filtered in the browser.
 *
 * - Search is debounced (300 ms). While a new search or client loads, the
 *   previous rows stay, dimmed (`aria-busy`), so typing never flashes a
 *   skeleton.
 * - Client filter only when the org has clients: a segmented control for a
 *   few (All clients, Your own, each client), the inventory client filter
 *   (Select, then a searchable Combobox past 6, DESIGN §13.8) for more.
 * - Load more, 50 a page, up to 200 rows on screen (DESIGN §14: a table past
 *   200 rows must virtualise; this one stops instead and points at search and
 *   the CSV, which holds every piece).
 * - Export CSV: every matching piece in the period (the server caps the file
 *   at 50,000 and says when it did).
 * - A failed read is an error with Retry, never an empty table (L8).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2, Search, Shirt } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { SegmentedControl, type SegmentedControlOption } from '@/components/ui/segmented-control';
import { EmptyState } from '@/components/shared/EmptyState';
import { SectionError } from '@/components/shared/SectionError';
import { toast } from '@/hooks/use-toast';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { downloadCsvFile } from '@/lib/download';
import { cn } from '@/lib/utils';
import { ClientFilterDropdown } from '@/features/inventory/components/ClientFilterDropdown';
import { ALL_CLIENTS } from '@/features/inventory/hooks/useClientOrgFilter';
import { useBillingFormat } from '../hooks/useBillingFormat';
import { billingQueryRetry } from '../lib/billing-errors';
import { billedPiecesExportFilename, buildBilledPiecesCsv } from '../lib/billing-export';
import { CARD_CLASS, SECONDARY_ACTION_CLASS, TEXT_TH } from '../lib/billing-ui';
import type { BilledPiece, BillPeriod } from '../lib/billing-view';

const PIECES_PAGE_SIZE = 50;
/** DESIGN §14: past 200 rows a table must virtualise; this one stops and points at search and the CSV. */
const PIECES_ROW_CAP = 200;
/** Up to this many clients the filter is a segmented control (All + Your own + clients). */
const SEGMENT_CLIENT_LIMIT = 4;
const SEARCH_DEBOUNCE_MS = 300;
/** "Your own" in the client filter (the org's own pieces). */
const OWN = '__own__';

const TD = 'px-3 py-2.5 align-top sm:px-4';

interface BillingPiecesTableProps {
  period: BillPeriod;
  isCurrent: boolean;
  /** The period's piece count when already known (the rail's); else the unfiltered total once read. */
  knownCount: number | null;
  /** Current month only: recorded but not yet with Stripe. */
  pendingCount: number;
  ownOrgId: string | null;
  /** The org's own name, for its own pieces in the CSV. */
  ownName: string;
}

function Dash() {
  return <span className="text-muted-foreground">-</span>;
}

export function BillingPiecesTable({
  period,
  isCurrent,
  knownCount,
  pendingCount,
  ownOrgId,
  ownName,
}: BillingPiecesTableProps) {
  const { t } = useTranslation('billing');
  const fmt = useBillingFormat();
  const utils = trpc.useUtils();
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const [client, setClient] = useState<string>(ALL_CLIENTS);
  const [exporting, setExporting] = useState(false);

  const clientOrgId = client === ALL_CLIENTS ? null : client === OWN ? ownOrgId : client;
  const filter = useMemo(
    () => ({ from: period.start, to: period.end, clientOrgId, search: query || null }),
    [period.start, period.end, clientOrgId, query],
  );

  const pieces = trpc.billing.listPieces.useInfiniteQuery(
    { ...filter, limit: PIECES_PAGE_SIZE },
    {
      getNextPageParam: (last) => last.nextCursor ?? undefined,
      placeholderData: (previous) => previous,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: billingQueryRetry,
    },
  );

  const pages = pieces.data?.pages ?? [];
  const rows: BilledPiece[] = pages.flatMap((page) => page.rows);
  const total = pages[0]?.totalCount ?? null;
  const clients = pages[0]?.clients ?? [];
  const isFiltered = query !== '' || client !== ALL_CLIENTS;
  const stale = pieces.isPlaceholderData;
  const periodCount = knownCount ?? (!isFiltered && !stale ? total : null);
  const showClientColumn = clients.length > 0 || rows.some((row) => !row.isOwnPiece);
  const canLoadMore = pieces.hasNextPage && rows.length < PIECES_ROW_CAP;
  const capped = pieces.hasNextPage && rows.length >= PIECES_ROW_CAP;

  const clientLabel = (row: BilledPiece): string =>
    row.clientName ?? (row.isOwnPiece ? t('pieces.ownPieces') : t('pieces.formerClient'));

  const clientName = (id: string): string => {
    if (id === OWN) return t('pieces.ownPieces');
    const found = clients.find((c) => c.id === id);
    return found?.name ?? t('pieces.formerClient');
  };

  const showAll = () => {
    setSearch('');
    setClient(ALL_CLIENTS);
  };

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // The vanilla client: a 50,000-row answer is used once, never cached.
      const result = await utils.client.billing.exportPieces.query(filter);
      downloadCsvFile(buildBilledPiecesCsv(result.rows, { ownName }), billedPiecesExportFilename(period));
      if (result.truncated) {
        toast({
          title: t('pieces.exportTruncated', { total: result.totalCount, shown: result.rows.length }),
          duration: Infinity,
        });
      }
    } catch {
      toast({ variant: 'destructive', title: t('pieces.exportError'), duration: Infinity });
    } finally {
      setExporting(false);
    }
  };

  // ── Client filter ───────────────────────────────────────────────────────────
  let clientFilter: ReactNode = null;
  if (clients.length > 0) {
    if (clients.length <= SEGMENT_CLIENT_LIMIT) {
      const options: Array<SegmentedControlOption<string>> = [
        { value: ALL_CLIENTS, label: t('pieces.allClients') },
        { value: OWN, label: t('pieces.ownPieces') },
        ...clients.map((c) => ({
          value: c.id,
          label: c.name ?? t('pieces.formerClient'),
          title: c.name ?? undefined,
        })),
      ];
      clientFilter = (
        <SegmentedControl
          options={options}
          value={client}
          onChange={setClient}
          ariaLabel={t('pieces.clientFilter')}
          className="max-w-full flex-wrap"
        />
      );
    } else {
      const names = new Map<string, string>(clients.map((c) => [c.id, c.name ?? t('pieces.formerClient')]));
      if (ownOrgId) names.set(ownOrgId, t('pieces.ownPieces'));
      clientFilter = (
        <ClientFilterDropdown
          value={client === OWN && ownOrgId ? ownOrgId : client}
          onChange={(next) => setClient(next === ownOrgId ? OWN : next)}
          ownOrgId={ownOrgId ?? undefined}
          orgIdToName={names}
          distinctOrgsInData={[...(ownOrgId ? [ownOrgId] : []), ...clients.map((c) => c.id)]}
          ariaLabel={t('pieces.clientFilter')}
        />
      );
    }
  }

  // ── Body ────────────────────────────────────────────────────────────────────
  let body: ReactNode;
  if (!pieces.data && pieces.isError) {
    body = (
      <div className="p-3 sm:p-4">
        <SectionError
          variant="compact"
          body={t('pieces.error')}
          onRetry={() => void pieces.refetch()}
          retrying={pieces.isFetching}
          retryLabel={t('retry')}
        />
      </div>
    );
  } else if (!pieces.data) {
    body = (
      <div role="status" aria-busy="true" className="divide-y">
        <span className="sr-only">{t('pieces.loading')}</span>
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex h-10 items-center gap-4 px-3 sm:px-4" aria-hidden="true">
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-3.5 w-16" />
            <Skeleton className="h-3.5 flex-1" />
          </div>
        ))}
      </div>
    );
  } else if (rows.length === 0 && isFiltered) {
    const clientText = client !== ALL_CLIENTS ? clientName(client) : null;
    const description =
      query && clientText
        ? t('pieces.filtered.both', { client: clientText, query })
        : query
          ? t('pieces.filtered.search', { query })
          : t('pieces.filtered.client', { client: clientText ?? '' });
    body = (
      <EmptyState
        size="sm"
        icon={Search}
        title={t('pieces.filtered.title')}
        description={description}
        className={cn(stale && 'opacity-60')}
        action={
          <Button type="button" variant="outline" className={SECONDARY_ACTION_CLASS} onClick={showAll}>
            {t('pieces.showAll')}
          </Button>
        }
      />
    );
  } else if (rows.length === 0) {
    body = (
      <EmptyState
        size="sm"
        icon={Shirt}
        title={isCurrent ? t('pieces.emptyCurrent.title') : t('pieces.emptyPast.title')}
        description={isCurrent ? t('pieces.emptyCurrent.description') : t('pieces.emptyPast.description')}
      />
    );
  } else {
    body = (
      <div
        aria-busy={stale || undefined}
        className={cn(
          'overflow-x-auto transition-opacity duration-200 motion-reduce:transition-none',
          stale && 'opacity-60',
        )}
      >
        <table className="w-full min-w-[48rem] border-collapse text-sm">
          <thead>
            <tr className="border-b">
              <th scope="col" className={TEXT_TH}>
                {t('pieces.col.batched')}
              </th>
              <th scope="col" className={TEXT_TH}>
                {t('pieces.col.order')}
              </th>
              <th scope="col" className={TEXT_TH}>
                {t('pieces.col.product')}
              </th>
              {showClientColumn && (
                <th scope="col" className={TEXT_TH}>
                  {t('pieces.col.client')}
                </th>
              )}
              <th scope="col" className={TEXT_TH}>
                {t('pieces.col.floor')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b last:border-b-0 hover:bg-muted/40">
                <td className={cn(TD, 'whitespace-nowrap tabular-nums')}>{fmt.dateTime(row.createdAt)}</td>
                <td className={cn(TD, 'whitespace-nowrap font-medium')}>{row.orderName ?? <Dash />}</td>
                <td className={cn(TD, 'min-w-[12rem] [overflow-wrap:anywhere]')}>
                  {row.productName ?? <Dash />}
                  {row.variantName && <span className="block text-xs text-muted-foreground">{row.variantName}</span>}
                </td>
                {showClientColumn && <td className={cn(TD, 'min-w-[9rem] break-words')}>{clientLabel(row)}</td>}
                <td className={cn(TD, 'min-w-[9rem] break-words')}>{row.locationName ?? <Dash />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const title = isCurrent ? t('pieces.titleCurrent') : t('pieces.titlePast');
  const periodText = fmt.period(period);
  const sub =
    periodCount !== null
      ? t('pieces.sub', { period: periodText, pieces: t('count.pieces', { count: periodCount }) })
      : periodText;

  return (
    <section aria-label={title} className={CARD_CLASS}>
      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b px-3 py-3 sm:px-4">
        <div className="min-w-0">
          <h3 className="text-base font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground tabular-nums">{sub}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          className={SECONDARY_ACTION_CLASS}
          onClick={() => void exportCsv()}
          disabled={exporting || !pieces.data || total === 0}
          aria-busy={exporting || undefined}
        >
          {exporting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          ) : (
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {t('pieces.export')}
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2.5 sm:px-4">
        <div className="relative w-full sm:w-64">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('pieces.search')}
            aria-label={t('pieces.search')}
            maxLength={100}
            className="h-8 pl-8 text-sm"
          />
        </div>
        {clientFilter}
      </div>

      {body}

      {pieces.isFetchNextPageError && (
        <div className="border-t px-3 py-2.5 sm:px-4">
          <SectionError
            variant="inline"
            body={t('pieces.loadMoreError')}
            onRetry={() => void pieces.fetchNextPage()}
            retrying={pieces.isFetchingNextPage}
            retryLabel={t('retry')}
          />
        </div>
      )}

      {rows.length > 0 && (
        <footer className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t px-3 py-2.5 sm:px-4">
          <div className="min-w-0 space-y-0.5 text-xs text-muted-foreground">
            <p role="status" className="tabular-nums">
              {total !== null
                ? t('pieces.showing', { shown: rows.length, total })
                : t('count.pieces', { count: rows.length })}
            </p>
            {isCurrent && pendingCount > 0 && <p>{t('pieces.pending', { count: pendingCount })}</p>}
            {capped && total !== null && <p>{t('pieces.capped', { shown: rows.length, total })}</p>}
          </div>
          {canLoadMore && (
            <Button
              type="button"
              variant="outline"
              className={SECONDARY_ACTION_CLASS}
              onClick={() => void pieces.fetchNextPage()}
              disabled={pieces.isFetchingNextPage}
              aria-busy={pieces.isFetchingNextPage || undefined}
            >
              {pieces.isFetchingNextPage && (
                <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              )}
              {t('pieces.loadMore')}
            </Button>
          )}
        </footer>
      )}
    </section>
  );
}
